import { queryDidDocument } from '@trustvc/w3c-issuer';
import jsonldSignatures from 'jsonld-signatures';
import attachmentsContext from '../context/attachments-context.json';
import bbsV1 from '../context/bbs-v1.json';
import bolContext from '../context/bill-of-lading.json';
import bolcContext from '../context/bill-of-lading-carrier.json';
import cooContext from '../context/coo.json';
import credentialsV1 from '../context/credentials-v1.json';
import credentialsV2 from '../context/credentials-v2.json';
import dataIntegrityV2 from '../context/data-integrity-v2.json';
import didV1 from '../context/did-v1.json';
import invoiceContext from '../context/invoice.json';
import jwsV1 from '../context/jws-2020-v1.json';
import multikeyV1 from '../context/multikey-v1.json';
import promissoryNoteContext from '../context/promissory-note.json';
import qrCodeContext from '../context/qrcode-context.json';
import renderContext from '../context/render-method-context.json';
import renderContextV2 from '../context/render-method-context-v2.json';
import statusList2021V1 from '../context/status-list-2021-v1.json';
import trContext from '../context/transferable-records-context.json';
import warehouseReceiptContext from '../context/warehouse-receipt.json';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
const DATA_INTEGRITY_V2_URL = "https://w3id.org/security/data-integrity/v2";
const DID_V1_URL = "https://www.w3.org/ns/did/v1";
const VC_V1_URL = "https://www.w3.org/2018/credentials/v1";
const VC_V2_URL = "https://www.w3.org/ns/credentials/v2";
const BBS_V1_URL = "https://w3id.org/security/bbs/v1";
const BLS12381_2020_V1_URL = "https://w3id.org/security/suites/bls12381-2020/v1";
const JWS_V1_URL = "https://w3id.org/security/suites/jws-2020/v1";
const MULTIKEY_V1_URL = "https://w3id.org/security/multikey/v1";
const STATUS_LIST_2021_CREDENTIAL_URL = "https://w3id.org/vc/status-list/2021/v1";
const TR_CONTEXT_URL = "https://trustvc.io/context/transferable-records-context.json";
const RENDER_CONTEXT_URL = "https://trustvc.io/context/render-method-context.json";
const RENDER_CONTEXT_V2_URL = "https://trustvc.io/context/render-method-context-v2.json";
const ATTACHMENTS_CONTEXT_URL = "https://trustvc.io/context/attachments-context.json";
const QRCODE_CONTEXT_URL = "https://trustvc.io/context/qrcode-context.json";
const BOL_CONTEXT_URL = "https://trustvc.io/context/bill-of-lading.json";
const BOLC_CONTEXT_URL = "https://trustvc.io/context/bill-of-lading-carrier.json";
const COO_CONTEXT_URL = "https://trustvc.io/context/coo.json";
const INVOICE_CONTEXT_URL = "https://trustvc.io/context/invoice.json";
const PROMISSORY_NOTE_CONTEXT_URL = "https://trustvc.io/context/promissory-note.json";
const WAREHOUSE_RECEIPT_CONTEXT_URL = "https://trustvc.io/context/warehouse-receipt.json";
const contexts = {
  [DATA_INTEGRITY_V2_URL]: dataIntegrityV2,
  [DID_V1_URL]: didV1,
  [VC_V1_URL]: credentialsV1,
  [VC_V2_URL]: credentialsV2,
  [BBS_V1_URL]: bbsV1,
  [BLS12381_2020_V1_URL]: bbsV1,
  [JWS_V1_URL]: jwsV1,
  [MULTIKEY_V1_URL]: multikeyV1,
  [STATUS_LIST_2021_CREDENTIAL_URL]: statusList2021V1
};
const trContexts = {
  [TR_CONTEXT_URL]: trContext
};
const renderContexts = {
  [RENDER_CONTEXT_URL]: renderContext
};
const renderContextsV2 = {
  [RENDER_CONTEXT_V2_URL]: renderContextV2
};
const attachmentsContexts = {
  [ATTACHMENTS_CONTEXT_URL]: attachmentsContext
};
const qrCodeContexts = {
  [QRCODE_CONTEXT_URL]: qrCodeContext
};
const templateContexts = {
  [BOL_CONTEXT_URL]: bolContext,
  [BOLC_CONTEXT_URL]: bolcContext,
  [COO_CONTEXT_URL]: cooContext,
  [INVOICE_CONTEXT_URL]: invoiceContext,
  [PROMISSORY_NOTE_CONTEXT_URL]: promissoryNoteContext,
  [WAREHOUSE_RECEIPT_CONTEXT_URL]: warehouseReceiptContext
};
const CredentialContextVersion = {
  v1: VC_V1_URL,
  v2: VC_V2_URL
};
async function getDocumentLoader(additionalContexts) {
  const resultMap = /* @__PURE__ */ new Map();
  [
    contexts,
    trContexts,
    renderContexts,
    renderContextsV2,
    attachmentsContexts,
    qrCodeContexts,
    templateContexts,
    additionalContexts
  ].forEach((context) => {
    if (!context) return;
    Object.entries(context).forEach(([url, document]) => {
      resultMap.set(url, {
        contextUrl: null,
        document,
        documentUrl: url
      });
    });
  });
  const resolveDid = /* @__PURE__ */ __name(async (did) => {
    const { wellKnownDid } = await queryDidDocument({
      did
    });
    if (did.includes("#")) {
      const verificationMethod = wellKnownDid.verificationMethod?.find((vm) => vm.id === did);
      if (!verificationMethod) {
        throw new Error(`Verification method ${did} could not be found.`);
      }
      const result = {
        contextUrl: null,
        document: {
          "@context": wellKnownDid["@context"],
          ...verificationMethod
        },
        documentUrl: did
      };
      resultMap.set(did, result);
      return result;
    } else {
      const result = {
        contextUrl: null,
        document: wellKnownDid,
        documentUrl: did
      };
      resultMap.set(did, result);
      return result;
    }
  }, "resolveDid");
  const customDocLoader = /* @__PURE__ */ __name(async (url) => {
    let result;
    if (resultMap.has(url)) {
      result = resultMap.get(url);
      return result;
    }
    if (url.includes("did:")) {
      return resolveDid(url);
    }
    const results = await fetch(url, {
      redirect: "follow"
    });
    const resolveContext = {
      contextUrl: null,
      document: await results.json(),
      documentUrl: results.url
    };
    resultMap.set(url, resolveContext);
    return resolveContext;
  }, "customDocLoader");
  return jsonldSignatures.extendContextLoader(customDocLoader);
}
__name(getDocumentLoader, "getDocumentLoader");

export { ATTACHMENTS_CONTEXT_URL, BBS_V1_URL, BLS12381_2020_V1_URL, BOLC_CONTEXT_URL, BOL_CONTEXT_URL, COO_CONTEXT_URL, CredentialContextVersion, DATA_INTEGRITY_V2_URL, DID_V1_URL, INVOICE_CONTEXT_URL, JWS_V1_URL, MULTIKEY_V1_URL, PROMISSORY_NOTE_CONTEXT_URL, QRCODE_CONTEXT_URL, RENDER_CONTEXT_URL, RENDER_CONTEXT_V2_URL, STATUS_LIST_2021_CREDENTIAL_URL, TR_CONTEXT_URL, VC_V1_URL, VC_V2_URL, WAREHOUSE_RECEIPT_CONTEXT_URL, attachmentsContexts, contexts, getDocumentLoader, qrCodeContexts, renderContexts, renderContextsV2, templateContexts, trContexts };
